import { ActionIcon, Divider, Flex, Tooltip } from "@mantine/core";
import {
  CircleDot,
  CircleOff,
  DivideSquare,
  Grip,
  MousePointer2Icon,
  Shapes,
  Type,
} from "lucide-react";
import { EditorModes, EditorModesOptions } from "../constants/types";
import React, { useCallback } from "react";

type ModeButtonConfig = {
  mode?: keyof EditorModes;
  icon?: React.ReactNode;
  label?: string;
  extraAction?: () => void;
  isDivider?: boolean;
};

type ModeTogglePanelProps = {
  editorModes: EditorModes;
  setEditorModes: React.Dispatch<React.SetStateAction<EditorModes>>;
  setBlockSelection: React.Dispatch<React.SetStateAction<boolean>>;
  t: (key: string) => string;
  disableAllEditorModes: () => void;
  handleDotInsertMode: () => void;
};

const ModeTogglePanel: React.FC<ModeTogglePanelProps> = ({
  editorModes,
  t,
  disableAllEditorModes,
  handleDotInsertMode,
  setEditorModes,
  setBlockSelection,
}: ModeTogglePanelProps) => {
  const mx = 10;
  const toggleEditorMode = useCallback(
    (mode: keyof EditorModes) => {
      setEditorModes((prev: EditorModes) => {
        const current: EditorModesOptions = prev[mode];
        const nextEnabled = !current.enabled;

        // Update block selection based on what the mode's selectionEnabled is
        if (current.selectionEnabled !== undefined) {
          setBlockSelection(!current.selectionEnabled);
        }

        return {
          ...prev,
          [mode]: {
            ...current,
            enabled: nextEnabled,
          },
        };
      });
    },
    [setBlockSelection, setEditorModes]
  );

  const buttons: ModeButtonConfig[] = [
    {
      mode: "selectItem",
      icon: <MousePointer2Icon size={20} />,
      label: t("scheme.redactor.modeTogglePanel.selectItemMode"),
    },
    {
      isDivider: true,
    },
    {
      mode: "dotInsert",
      icon: <Grip size={20} />,
      label: t("scheme.redactor.modeTogglePanel.dotInsertMode"),
      extraAction: handleDotInsertMode,
    },
    {
      mode: "insertSeatInRowOrCol",
      icon: <CircleDot size={20} />,
      label: t("scheme.redactor.modeTogglePanel.insertSeatInRowOrColMode"),
    },
    {
      mode: "deleteCircle",
      icon: <CircleOff size={20} />,
      label: t("scheme.redactor.modeTogglePanel.deleteCircle"),
    },
    {
      isDivider: true,
    },
    {
      mode: "textInsert",
      icon: <Type size={20} />,
      label: t("scheme.redactor.modeTogglePanel.textInsertMode"),
    },
    {
      mode: "shapeInsert",
      icon: <Shapes size={20} />,
      label: t("scheme.redactor.modeTogglePanel.shapeInsertMode"),
    },
    {
      mode: "fanZone",
      icon: <DivideSquare size={20} />,
      label: t("scheme.redactor.modeTogglePanel.fanZone"),
    },
  ];

  return (
    <Flex gap={10} wrap="wrap" direction="column">
      <Divider />

      {buttons.map((btn, idx) =>
        btn.isDivider ? (
          <Divider key={`divider-${idx}`} />
        ) : (
          <Tooltip key={btn.mode} label={btn.label} position="right">
            <ActionIcon
              mx={mx}
              variant={
                editorModes[btn.mode as keyof EditorModes].enabled
                  ? "filled"
                  : "light"
              }
              onClick={() => {
                disableAllEditorModes();
                toggleEditorMode(btn.mode as keyof EditorModes);
                btn.extraAction?.();
              }}
            >
              {btn.icon}
            </ActionIcon>
          </Tooltip>
        )
      )}
    </Flex>
  );
};

export default React.memo(ModeTogglePanel);
